import { safelyGetCurrentPosition } from '../current-position';
import { searchLocationsByCurrentPosition } from '../locations';
import { getRecentLocations } from '../recent-locations';

import type { Location, LocationGroups, PositionCoordinates } from '../../LocationSearch.types';
import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

export async function getRecentAndNearbyLocations(
  params: GetInitialLocationsParams,
): Promise<RecentAndNearbyLocations> {
  const { client, shouldRequestLocationPermission = true } = params;

  // ─── Locations ───────────────────────────────────────────────────────

  const nearbyLocationsPromise = shouldRequestLocationPermission
    ? getNearbyLocations({
        client,
        shouldRequestLocationPermission,
      })
    : Promise.resolve({
        nearbyLocations: EMPTY_LOCATION_GROUPS,
        currentPositionCoordinates: undefined,
      });
  const recentLocationsPromise = getRecentLocations({
    client,
  });

  const [nearbyLocationsData, recentLocationsData] = await Promise.all([
    nearbyLocationsPromise,
    recentLocationsPromise,
  ]);

  return {
    currentPositionCoordinates: nearbyLocationsData.currentPositionCoordinates,
    nearbyLocations: nearbyLocationsData.nearbyLocations,
    recentLocations: recentLocationsData.recentLocations,
  };
}

export async function getNearbyLocations(
  params: GetInitialLocationsParams,
): Promise<NearbyLocations & CurrentPositionCoordinates> {
  const { client, shouldRequestLocationPermission = true } = params;

  if (!shouldRequestLocationPermission) {
    return { nearbyLocations: EMPTY_LOCATION_GROUPS, currentPositionCoordinates: undefined };
  }

  // ─── Current Position ────────────────────────────────────────────────

  const currentPosition = await safelyGetCurrentPosition();
  const currentPositionCoordinates = currentPosition?.coords;

  // ─── Locations ───────────────────────────────────────────────────────

  const { nearbyLocations } = await searchLocationsByCurrentPosition({
    client,
    currentPositionCoordinates,
  });

  return {
    nearbyLocations,
    currentPositionCoordinates,
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const EMPTY_LOCATION_GROUPS: LocationGroups = { pickup: [], outpost: [] };

// ─── Types ───────────────────────────────────────────────────────────────────

type GetInitialLocationsParams = {
  client: Client;
  shouldRequestLocationPermission?: boolean;
};

type RecentAndNearbyLocations = RecentLocations & NearbyLocations & CurrentPositionCoordinates;

type RecentLocations = { recentLocations: Location[] };

type NearbyLocations = { nearbyLocations: LocationGroups };

type CurrentPositionCoordinates = {
  currentPositionCoordinates: PositionCoordinates | undefined;
};
